import gaAnalytics from "@analytics/google-analytics";
import gtmAnalytics from "@analytics/google-tag-manager";
import analytics from "analytics";
import { useEffect, useMemo } from "react";

import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { userFullName } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";

import { equidefiGAAnalytics } from "@/lib/equidefiGAAnalytics";

const APP_NAME = "eq-investor-portal";

// eslint-disable-next-line react-hooks/exhaustive-deps
const ANALYTICS_CONFIG_MAP = {
  GOOGLE_TAG_MANAGER: (config: { key: string }) => gtmAnalytics({ containerId: config.key }),
  GOOGLE_ANALYTICS: (config: { key: string }) => gaAnalytics({ measurementIds: [config.key] }),
};

export const useOfferingAnalytics = (offering: DTO.Offering, investment: DTO.Investment) => {
  const { data: user } = useCurrentUser();

  const plugins = useMemo(() => {
    return (
      offering?.analytics
        ?.map((config) => {
          const analyticDefinition = ANALYTICS_CONFIG_MAP[config.type as keyof typeof ANALYTICS_CONFIG_MAP];
          if (!analyticDefinition) return false;

          return analyticDefinition(config);
        })
        ?.filter(Boolean) ?? []
    );
  }, [offering]);

  const eqAnalytics = useMemo(() => {
    const instance = analytics({
      app: APP_NAME,
      plugins,
    });

    if (process.env.NODE_ENV === "development") {
      instance.on("identify", (payload) => {
        // console.info("Analytics identify event:", payload);
      });

      instance.on("page", (payload) => {
        // console.info("Analytics page event:", payload);
      });

      instance.on("track", (payload) => {
        // console.info("Analytics track event:", payload);
      });
    }

    return instance;
  }, [plugins]);

  useEffect(() => {
    if (user?.id) {
      eqAnalytics.identify(user.id, {
        email: user.email,
        phone: user.phone,
        name: userFullName(user, { middle: "none" }),
      });
    }
  }, [eqAnalytics, user]);

  return {
    ...eqAnalytics,
    track: (event: string, properties: Record<string, unknown> = {}) => {
      eqAnalytics.track(event, {
        investment_id: investment?.id,
        amount: investment?.amount,
        quantity: investment?.quantity,
        ...properties,
      });
      equidefiGAAnalytics?.track(event, {
        investment_id: investment?.id,
        amount: investment?.amount,
        quantity: investment?.quantity,
        ...properties,
      });
    },
  };
};
